import React, { useMemo, useRef, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnDef,
  SortingState,
  RowSelectionState,
  OnChangeFn,
  Table,
} from '@tanstack/react-table';
import { PersonalBusinessClassificationChip } from '../components';
import { DataEntryRowDataType, JournalEntryClassificationValue } from '../types';
import { Checkbox } from '../PersonalBusinessReviewView.styles';
import { formatCurrency, formatDate } from '../utils/formatters';

interface UsePersonalBusinessTableProps {
  data: DataEntryRowDataType[];
  onRowClick: (row: DataEntryRowDataType) => void;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  rowSelection: RowSelectionState;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}

interface UsePersonalBusinessTableReturn {
  table: Table<DataEntryRowDataType>;
  columns: ColumnDef<DataEntryRowDataType>[];
}

export const usePersonalBusinessTable = ({
  data,
  onRowClick,
  onRowSelectionChange,
  rowSelection,
  sorting,
  onSortingChange,
}: UsePersonalBusinessTableProps): UsePersonalBusinessTableReturn => {
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  const columns = useMemo(
    (): ColumnDef<DataEntryRowDataType>[] => [
      // Selection column
      {
        id: 'select',
        size: 40,
        header: ({ table }) => (
          <Checkbox
            ref={selectAllCheckboxRef}
            type="checkbox"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <div
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
            role="button"
            tabIndex={-1}
          >
            <Checkbox
              type="checkbox"
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              ref={input => {
                if (input) input.indeterminate = row.getIsSomeSelected();
              }}
              onChange={row.getToggleSelectedHandler()}
            />
          </div>
        ),
      },

      // Date column
      {
        accessorKey: 'date',
        id: 'date',
        size: 80,
        header: 'Date',
        cell: info => formatDate(info.getValue() as Date, 'MMM DD'),
        sortingFn: 'datetime',
      },

      // Description column
      {
        accessorKey: 'description',
        id: 'description',
        header: 'Description',
        cell: info => info.getValue(),
        filterFn: 'includesString',
      },

      // Account column
      {
        accessorFn: row => row.ledgerAccount.name,
        id: 'account',
        size: 200,
        header: 'Account',
        cell: info => info.getValue(),
        filterFn: 'includesString',
      },

      // Classification column
      {
        accessorFn: row => row.classification.value,
        id: 'classification',
        size: 100,
        header: 'Type',
        cell: info => (
          <PersonalBusinessClassificationChip
            type={info.getValue() as JournalEntryClassificationValue}
          />
        ),
        filterFn: 'equals',
      },

      // Amount column
      {
        accessorKey: 'amount',
        id: 'amount',
        size: 120,
        header: 'Amount',
        cell: info => formatCurrency(info.getValue() as number),
        sortingFn: 'basic',
      },
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
      sorting,
    },
    enableRowSelection: true,
    enableMultiRowSelection: true,
    onRowSelectionChange,
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: row => row.id,
    enableSorting: true,
    enableColumnFilters: true,
    meta: {
      onRowClick,
    },
  });

  // Update indeterminate state whenever selection changes
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const isAllSelected = table.getIsAllRowsSelected();
      const isSomeSelected = table.getIsSomeRowsSelected();

      selectAllCheckboxRef.current.checked = isAllSelected;
      selectAllCheckboxRef.current.indeterminate = !isAllSelected && isSomeSelected;
    }
  }, [rowSelection, table]);

  return {
    table,
    columns,
  };
};
